import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ART_TIER_COUNT } from '../../shared/layer-stack'
import type { AmendOptionInput, BoOption } from '../../shared/types'

export interface AmendOptionDialogProps {
  open: boolean
  setId: string
  option?: BoOption
  busy?: boolean
  error?: string
  onCancel: () => void
  onSave: (input: AmendOptionInput) => void
}

export default function AmendOptionDialog({
  open,
  setId,
  option,
  busy = false,
  error,
  onCancel,
  onSave,
}: AmendOptionDialogProps) {
  const { formatMessage } = useIntl()
  const [displayName, setDisplayName] = useState('')
  const [minTier, setMinTier] = useState('')

  useEffect(() => {
    if (open && option) {
      setDisplayName(option.displayName)
      setMinTier(
        option.visibility?.minTier === undefined
          ? ''
          : String(option.visibility.minTier),
      )
    }
  }, [open, option])

  if (!option) {
    return null
  }

  const nameChanged = displayName !== option.displayName
  const tierChanged =
    minTier !== (option.visibility?.minTier === undefined ? '' : String(option.visibility.minTier))
  const nothingToSave = !nameChanged && !tierChanged
  const nameValid = displayName.trim().length > 0 && displayName.length <= 120

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth={true}>
      <DialogTitle>
        {formatMessage({ id: 'avatarStudio.amend.title' }, { name: option.displayName })}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="small"
            autoFocus={true}
            label={formatMessage({ id: 'avatarStudio.wizard.displayName' })}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={!nameValid}
            helperText={
              nameValid ? ' ' : formatMessage({ id: 'avatarStudio.amend.nameRequired' })
            }
            inputProps={{ maxLength: 120 }}
            disabled={busy}
          />
          <TextField
            select={true}
            size="small"
            label={formatMessage({ id: 'avatarStudio.amend.minTier' })}
            value={minTier}
            onChange={(e) => setMinTier(e.target.value)}
            helperText={formatMessage({ id: 'avatarStudio.amend.minTierHint' })}
            disabled={busy}
          >
            <MenuItem value="">
              {formatMessage({ id: 'avatarStudio.amend.minTierNone' })}
            </MenuItem>
            {Array.from({ length: ART_TIER_COUNT }, (_v, tier) => (
              <MenuItem key={tier} value={String(tier)}>
                {formatMessage({ id: 'avatarStudio.option.tierBadge' }, { tier })}
              </MenuItem>
            ))}
          </TextField>
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              {formatMessage({ id: 'avatarStudio.wizard.slug' })}
            </Typography>
            <Typography variant="body2" translate="no">
              {option.slug}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessage({ id: 'avatarStudio.amend.slugFixed' })}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {formatMessage({ id: 'avatarStudio.editor.cancel' })}
        </Button>
        <Button
          variant="contained"
          disabled={busy || nothingToSave || !nameValid}
          onClick={() =>
            onSave({
              setId,
              category: option.category,
              slug: option.slug,
              ...(nameChanged ? { displayName: displayName.trim() } : {}),
              ...(tierChanged
                ? {
                    visibility:
                      minTier === ''
                        ? {}
                        : { ...option.visibility, minTier: Number(minTier) },
                  }
                : {}),
            })
          }
        >
          {formatMessage({ id: 'avatarStudio.amend.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
